import { useMemo } from 'react';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { JiraFY27H1HoldoutExclusionTrigger } from '@/components/signup/jira-fy27h1-holdout-exclusion-trigger/jira-fy27h1-holdout-exclusion-trigger';
import { ProvisioningFunnelUfoProvider } from '@/components/signup/provisioning-funnel-ufo/provisioning-funnel-ufo';
import { ProductKey } from '@/components/signup/utils/products';
import { ExperimentView } from '@/utils/experiment/experiment-view';
import { experiments } from '@/utils/experiment/manifest';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { AnalyticsClientUpdater } from '../../components/analytics-client-updater';
import { AutoSelectThemePreference } from '../../components/auto-select-theme-preference';
import { useCloudStatus } from '../../hooks/use-cloud-status';
import { useCloudStatusRedirection } from '../../hooks/use-cloud-status-redirection';
import {
	TenantAwareStatsigClientUpdaterProvider,
	useTenantAwareStatsigClientUpdater,
} from '../../hooks/use-tenant-aware-statsig-client-updater';
import { PerceivedProvisioningStartTimer } from '../../utils/perceived-provisioning/perceived-provisioning-start-timer';
import { jiraScreenConfig } from '../../utils/screen-manager/jira-screen-config';
import { jiraScreenDictionary } from '../../utils/screen-manager/jira-screen-dictionary';
import { DeferSiteNamingGate } from '../jira/defer-site-naming-gate/defer-site-naming-gate';
import { JiraAnimatedProvisioningScreen } from '../jira/jira-animated-provisioning-screen/jira-animated-provisioning-screen';
import { ProductRedirector } from '../product-redirector/product-redirector';

import { BaseProvisioningUI } from './base-provisioning-ui';

/**
 * Component that shows provisioning UI for Jira.
 * @returns @type {JSX.Element}
 */
const ProvisioningContent = () => {
	const { cloudId } = useCloudStatus();
	const isHoldoutExclusionEnabled = checkFeatureFlagGate(featureFlags.jiraFY27H1HoldoutExclusion);
	const { signupSource } = useSearchParameters();

	// DeferSiteNamingGate (D1D6-47, ATLAS-126008) intercepts the render when
	// the user is in the deferred variant — it shows the deferred loading
	// view while `useDeferSiteActivation` runs, then rewrites the URL via
	// `history.replaceState` and falls through to the normal Jira flow.
	// For 100% of current prod traffic (no `?deferSiteNaming=true`) this is
	// a transparent passthrough.
	return (
		<DeferSiteNamingGate>
			<>
				<AutoSelectThemePreferenceWrapper />
				<AnalyticsClientUpdater />
				{isHoldoutExclusionEnabled && signupSource === 'jira-ai-native' && (
					<JiraFY27H1HoldoutExclusionTrigger cloudId={cloudId} />
				)}
				<BaseProvisioningUI
					screenConfig={jiraScreenConfig}
					screenDictionary={jiraScreenDictionary}
					screenFallback={
						<PerceivedProvisioningStartTimer>
							<ProductRedirector>
								<JiraAnimatedProvisioningScreen />
							</ProductRedirector>
						</PerceivedProvisioningStartTimer>
					}
				/>
			</>
		</DeferSiteNamingGate>
	);
};

export const JiraProvisioningUI = () => {
	const { addBlockingRedirectPromise } = useCloudStatusRedirection();
	const isProvisioningFunnelUfoEnabled = checkFeatureFlagGate(
		featureFlags.provisioningFunnelUfoImprovements,
	);

	return (
		<TenantAwareStatsigClientUpdaterProvider>
			{isProvisioningFunnelUfoEnabled ? (
				<ProvisioningFunnelUfoProvider
					initialCustomData={{ productKey: ProductKey.JIRA_SOFTWARE }}
					addBlockingRedirectPromise={addBlockingRedirectPromise}
				>
					<ProvisioningContent />
				</ProvisioningFunnelUfoProvider>
			) : (
				<ProvisioningContent />
			)}
		</TenantAwareStatsigClientUpdaterProvider>
	);
};

const AutoSelectThemePreferenceWrapper = () => {
	const { updatedStatsigClientWithCloudId } = useTenantAwareStatsigClientUpdater();
	const { cloudId, createCloudStatus } = useCloudStatus();
	const { addBlockingRedirectPromise } = useCloudStatusRedirection();

	const experimentPreconditionsMet = useMemo(
		() => cloudId && updatedStatsigClientWithCloudId && createCloudStatus === 'success',
		[cloudId, createCloudStatus, updatedStatsigClientWithCloudId],
	);

	// experimentPreconditionsMet ensures that the site and statsig client is ready before we enroll and try to hit
	// the Jira theme preference API. Otherwise, it will throw an error if the tenant is not ready.
	return experimentPreconditionsMet ? (
		<ExperimentView
			experiment={experiments.jiraOnboardingAutodetectSystemTheme}
			render={(value) => {
				if (value) {
					return (
						<AutoSelectThemePreference
							cloudId={cloudId}
							product="jira"
							onRequest={addBlockingRedirectPromise}
						/>
					);
				}

				return null;
			}}
		/>
	) : null;
};
